import { ChangeDetectorRef, Component, NgZone, OnDestroy } from '@angular/core';
import { of, Subject, timer } from 'rxjs';
import { catchError, exhaustMap, takeUntil, takeWhile } from 'rxjs/operators';
import { EmailAutomationStatus, InsuranceService } from 'src/app/Services/insurance.service';
import { AuthService } from '../../auth/AuthService';
import swal from 'sweetalert2';

type EmailAutomationLogType = 'start' | 'end' | 'info' | 'error';

@Component({
  selector: 'pm-email-automation',
  templateUrl: './email-automation.component.html',
  styleUrls: ['./email-automation.component.css']
})
export class EmailAutomationComponent implements OnDestroy {
  selectedDate: string | null = null;
  senderEmail = '';
  senderPassword = '';
  showPassword = false;
  emailRunning = false;
  emailJobId: number | null = null;
  emailJobStatus: string | null = null;
  emailStatus: EmailAutomationStatus | null = null;

  private readonly destroy$ = new Subject<void>();
  private lastProgress = '';
  private lastFailureDetails = '';
  private statusReadErrorLogged = false;

  emailLogs: Array<{
    time: string;
    message: string;
    type: EmailAutomationLogType;
  }> = [];

  constructor(
    private insuranceServices: InsuranceService,
    public authService: AuthService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  runEmailAutomation(): void {
    const clientId = this.authService.clientId;
    const email = this.senderEmail.trim();

    if (
      !clientId ||
      !this.selectedDate ||
      !email ||
      !this.senderPassword ||
      this.emailRunning
    ) {
      return;
    }

    this.ngZone.run(() => {
      this.emailRunning = true;
      this.emailJobId = null;
      this.emailJobStatus = null;
      this.emailStatus = null;
      this.lastProgress = '';
      this.lastFailureDetails = '';
      this.statusReadErrorLogged = false;
      this.addLog(`Email automation started for ${this.selectedDate}.`, 'start');
      this.addLog(`Sender account: ${email}`, 'info');
      this.addLog('Using desktop folder "eligibility Reports". No file upload is required.', 'info');
      this.cdr.detectChanges();
    });

    this.insuranceServices
      .runEmailAutomation(
        clientId,
        email,
        this.senderPassword,
        this.selectedDate
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        response => {
          const jobId = Number(response?.jobId);

          if (!Number.isSafeInteger(jobId) || jobId <= 0) {
            this.handleRunError(response?.message || 'The email automation response did not include a valid job ID.');
            return;
          }

          this.ngZone.run(() => {
            this.emailJobId = jobId;
            this.emailJobStatus = response.status || 'INITIATED';
            this.addLog(`Email automation job #${jobId} accepted. Waiting for processing to finish.`, 'info');
            this.cdr.detectChanges();
          });

          this.pollJobStatus(jobId);
        },
        (error: any) => this.handleRunError(error?.error?.message || error?.error)
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private pollJobStatus(jobId: number): void {
    timer(0, 60000)
      .pipe(
        // Allow a slow request to finish before issuing another status read.
        exhaustMap(() => this.insuranceServices.getEmailAutomationStatus(jobId).pipe(
          catchError(() => {
            this.ngZone.run(() => {
              if (!this.statusReadErrorLogged) {
                this.statusReadErrorLogged = true;
                this.addLog(`Unable to read status for job #${jobId}. Retrying automatically.`, 'error');
                this.cdr.detectChanges();
              }
            });
            // A status read failure does not mean the background email job failed.
            return of(null);
          })
        )),
        takeWhile(status => !status || !this.isJobFinished(status), true),
        takeUntil(this.destroy$)
      )
      .subscribe(status => {
        if (status) {
          this.ngZone.run(() => this.updateJobStatus(status));
        }
      });
  }

  private isJobFinished(status: EmailAutomationStatus): boolean {
    return status.completed === true ||
      ['SUCCESS', 'COMPLETED', 'FAILED', 'NOT_FOUND', 'CANCELLED'].includes((status.status || '').toUpperCase());
  }

  private updateJobStatus(status: EmailAutomationStatus): void {
    this.statusReadErrorLogged = false;
    this.emailStatus = status;
    this.emailJobStatus = status.status || 'PENDING';

    const progress = `Job #${status.jobId}: ${this.emailJobStatus} | Processed ${status.processedEmails || 0}/${status.totalEmails || 0} emails | Successful: ${status.successfulEmails || 0} | Failed: ${status.failedEmails || 0}.`;
    if (progress !== this.lastProgress) {
      this.lastProgress = progress;
      this.addLog(progress, 'info');
    }

    if (status.failureDetails && status.failureDetails !== this.lastFailureDetails) {
      this.lastFailureDetails = status.failureDetails;
      this.addLog(status.failureDetails, 'error');
    }

    if (this.isJobFinished(status)) {
      this.emailRunning = false;
      const result = (status.status || '').toUpperCase();
      if (result === 'SUCCESS' || result === 'COMPLETED') {
        this.addLog(`Email automation job #${status.jobId} completed. Successful: ${status.successfulEmails || 0}; Failed: ${status.failedEmails || 0}.`, 'end');
      } else {
        this.addLog(result === 'NOT_FOUND'
          ? `Email automation job #${status.jobId} was not found.`
          : `Email automation job #${status.jobId} finished with status ${this.emailJobStatus}.`, 'error');
      }
    }

    this.cdr.detectChanges();
  }

  private handleRunError(errorMessage: any): void {
    this.ngZone.run(() => {
      this.emailRunning = false;
      this.emailJobStatus = 'FAILED';
      const message = typeof errorMessage === 'string' && errorMessage
        ? errorMessage : 'Email automation request failed.';
      this.addLog(message, 'error');
      this.cdr.detectChanges();
      swal.fire({ title: 'Error!', icon: 'error', text: message, showConfirmButton: false, timer: 5000 });
    });
  }

  clearLogs(): void {
    this.emailLogs = [];
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  private addLog(
    message: string,
    type: EmailAutomationLogType
  ): void {
    this.emailLogs.unshift({
      time: new Date().toLocaleString(),
      message,
      type
    });
  }
}
